import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Shield, Key, Check, X, Copy, AlertTriangle, Lock } from 'lucide-react';

export default function TOTPSetupPage({ user }) {
  const [totpStatus, setTotpStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [setupStep, setSetupStep] = useState('status'); // status, setup, verify, backup, disable_verify

  // Setup state
  const [totpSecret, setTotpSecret] = useState(null);
  const [otpauthUrl, setOtpauthUrl] = useState(null);
  /*
   * ⛔⛔ THE QR WAS NEVER RENDERED — Card P162 (hotfix).
   *
   * This page has told users "Scan the QR code or manually enter the secret
   * key below" since it was written, and showed only the secret and the
   * otpauth URI as text. There was no <img>, no canvas, and `qrcode` — a
   * dependency the repo already carries — was imported nowhere in src/.
   *
   * ⚠ So nothing BROKE. It was never built, and the instruction above it
   * made that look like a failure rather than an omission, which is why it
   * reads as "the QR is not generating".
   *
   * ⛔ Rendered CLIENT-SIDE from the URI the server already returns. The
   * alternative — having the server send a PNG — would put the secret in a
   * second representation, in a second place, for no gain: the browser
   * already holds the URI it is encoding.
   */
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrFailed, setQrFailed] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const [disableCode, setDisableCode] = useState('');

  /*
   * ⚠ A FAILED RENDER SAYS SO. toDataURL can reject (a URI too long for the
   * chosen error-correction level, a canvas the browser refuses). Swallowing
   * that would leave an empty box — which is exactly the symptom this card
   * was raised for — so the manual-entry path is named instead.
   */
  useEffect(() => {
    let cancelled = false;
    if (!otpauthUrl) { setQrDataUrl(null); setQrFailed(false); return undefined; }
    QRCode.toDataURL(otpauthUrl, { errorCorrectionLevel: 'M', margin: 1, width: 220 })
      .then((url) => { if (!cancelled) { setQrDataUrl(url); setQrFailed(false); } })
      .catch(() => { if (!cancelled) { setQrDataUrl(null); setQrFailed(true); } });
    return () => { cancelled = true; };
  }, [otpauthUrl]);

  useEffect(() => {
    fetchTotpStatus();
  }, []);

  const fetchTotpStatus = async () => {
    setLoading(true);
    try {
      const status = await window.zignagenixAPI.totpStatus();
      setTotpStatus(status);
      setSetupStep('status');
    } catch (error) {
      console.error('Error fetching TOTP status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnableTOTP = async () => {
    setLoading(true);
    try {
      const result = await window.zignagenixAPI.totpGenerate();
      setTotpSecret(result.secret);
      setOtpauthUrl(result.otpauthUrl);
      setBackupCodes(result.backupCodes || []);
      setSetupStep('setup');
    } catch (error) {
      console.error('Error generating TOTP:', error);
      alert('Failed to generate TOTP secret');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      alert('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await window.zignagenixAPI.totpVerifyAndEnable(verificationCode);
      // Backup codes were captured from totp:generate in handleEnableTOTP;
      // totp:verify-enable returns only { success: true }
      setSetupStep('backup');
      setVerificationCode('');
    } catch (error) {
      console.error('Error verifying TOTP:', error);
      alert('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableTOTP = async () => {
    if (!disableCode.trim() || disableCode.length !== 6) {
      alert('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await window.zignagenixAPI.totpDisable(disableCode);
      await fetchTotpStatus();
      setDisableCode('');
    } catch (error) {
      console.error('Error disabling TOTP:', error);
      alert('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartDisable = () => {
    setSetupStep('disable_verify');
    setDisableCode('');
  };

  const handleCompleteSetup = async () => {
    // Return to status view
    await fetchTotpStatus();
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const copyBackupCodes = () => {
    const text = (backupCodes || []).join('\n');
    copyToClipboard(text, 'backup');
  };

  if (loading && setupStep === 'status') {
    return (
      <div className="page">
        <h1 className="page-title">Two-Factor Authentication Setup</h1>
        <div className="loading-text">Loading authentication status...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">Two-Factor Authentication Setup</h1>

      {/* Status View */}
      {setupStep === 'status' && (
        <section className="totp-section">
          <div className="status-card">
            <div className="status-header">
              <Shield size={32} />
              <div>
                <h2>Current 2FA Status</h2>
                <p>
                  {totpStatus?.enabled ? (
                    <span className="status-enabled">Enabled</span>
                  ) : (
                    <span className="status-disabled">Disabled</span>
                  )}
                </p>
              </div>
            </div>

            {!totpStatus?.enabled && (
              <div className="info-box">
                <p>
                  Two-factor authentication adds an extra layer of security to your account. You'll need to enter a code from an authenticator app in addition to your password when signing in.
                </p>
              </div>
            )}

            {totpStatus?.enabled && (
              <div className="success-box">
                <Check size={20} />
                <p>Two-factor authentication is active on your account.</p>
              </div>
            )}

            <div className="action-buttons">
              {!totpStatus?.enabled && (
                <button
                  className="btn-primary"
                  onClick={handleEnableTOTP}
                  disabled={loading}
                >
                  <Key size={18} /> Enable 2FA
                </button>
              )}

              {totpStatus?.enabled && (
                <button
                  className="btn-danger"
                  onClick={handleStartDisable}
                >
                  <X size={18} /> Disable 2FA
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Setup Step: Display Secret */}
      {setupStep === 'setup' && (
        <section className="totp-section">
          <div className="setup-card">
            <div className="step-header">
              <h2>Step 1: Add Authenticator Account</h2>
              <p>Scan the QR code or manually enter the secret key below</p>
            </div>

            {qrDataUrl ? (
              <div className="qr-section" style={{ textAlign: 'center', margin: '16px 0' }}>
                {/* ⚠ alt describes the control, never the secret: alt text is
                    read aloud, copied by scrapers and shown when the image
                    fails. The secret must not travel in it. */}
                <img
                  src={qrDataUrl}
                  alt="QR code for authenticator enrolment"
                  data-testid="totp-qr"
                  width={220}
                  height={220}
                  style={{ borderRadius: 8, border: '1px solid #E3E7EE', background: '#fff' }}
                />
              </div>
            ) : null}

            {qrFailed ? (
              <div className="warning-box" data-testid="totp-qr-failed">
                <AlertTriangle size={20} />
                <p>
                  The QR code could not be drawn in this browser. Enter the secret key
                  below into your authenticator app by hand — it is the same credential.
                </p>
              </div>
            ) : null}

            <div className="warning-box">
              <AlertTriangle size={20} />
              <p>Save this secret key in a secure location. You'll need it if you lose access to your authenticator app.</p>
            </div>

            <div className="secret-section">
              <div className="secret-item">
                <label>Secret Key (Manual Entry)</label>
                <div className="secret-display">
                  <code>{totpSecret}</code>
                  <button
                    className="btn-sm btn-ghost"
                    onClick={() => copyToClipboard(totpSecret, 'secret')}
                  >
                    {copiedField === 'secret' ? (
                      <>
                        <Check size={16} /> Copied
                      </>
                    ) : (
                      <>
                        <Copy size={16} /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {otpauthUrl && (
                <div className="secret-item">
                  <label>Authenticator URI</label>
                  <div className="secret-display">
                    <code style={{ fontSize: '11px', wordBreak: 'break-all' }}>{otpauthUrl}</code>
                    <button
                      className="btn-sm btn-ghost"
                      onClick={() => copyToClipboard(otpauthUrl, 'url')}
                    >
                      {copiedField === 'url' ? (
                        <>
                          <Check size={16} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={16} /> Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="info-box">
              <p>Popular authenticator apps: Google Authenticator, Microsoft Authenticator, Authy, 1Password</p>
            </div>

            <button
              className="btn-primary"
              onClick={() => setSetupStep('verify')}
            >
              Next: Verify Code
            </button>
          </div>
        </section>
      )}

      {/* Setup Step: Verify Code */}
      {setupStep === 'verify' && (
        <section className="totp-section">
          <div className="setup-card">
            <div className="step-header">
              <h2>Step 2: Verify Your Code</h2>
              <p>Enter the 6-digit code from your authenticator app</p>
            </div>

            <div className="form-group">
              <label className="form-label">Verification Code</label>
              <input
                type="text"
                className="form-input code-input"
                maxLength="6"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
              />
            </div>

            <div className="button-group">
              <button
                className="btn-primary"
                onClick={handleVerifyAndEnable}
                disabled={loading || verificationCode.length !== 6}
              >
                <Check size={18} /> Verify & Enable 2FA
              </button>
              <button
                className="btn-ghost"
                onClick={() => {
                  setSetupStep('status');
                  setTotpSecret(null);
                  setOtpauthUrl(null);
                  setVerificationCode('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Setup Step: Backup Codes */}
      {setupStep === 'backup' && (
        <section className="totp-section">
          <div className="setup-card">
            <div className="step-header">
              <h2>Step 3: Save Backup Codes</h2>
              <p>Store these backup codes in a safe place. Use them if you can't access your authenticator.</p>
            </div>

            <div className="warning-box">
              <AlertTriangle size={20} />
              <p>These backup codes will only be shown once. Download or write them down before closing this screen.</p>
            </div>

            <div className="backup-codes-container">
              <div className="backup-codes-list">
                {(backupCodes || []).map((code, idx) => (
                  <div key={idx} className="backup-code">
                    {code}
                  </div>
                ))}
              </div>
              <button
                className="btn-secondary btn-sm"
                onClick={copyBackupCodes}
              >
                {copiedField === 'backup' ? (
                  <>
                    <Check size={16} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} /> Copy All Codes
                  </>
                )}
              </button>
            </div>

            <div className="button-group">
              <button
                className="btn-primary"
                onClick={handleCompleteSetup}
              >
                <Check size={18} /> I've Saved My Codes
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Disable 2FA: Verify Code */}
      {setupStep === 'disable_verify' && (
        <section className="totp-section">
          <div className="setup-card">
            <div className="step-header danger">
              <h2>Disable Two-Factor Authentication</h2>
              <p>Enter your current 6-digit code to confirm disabling 2FA</p>
            </div>

            <div className="warning-box danger">
              <AlertTriangle size={20} />
              <p>Disabling 2FA will reduce the security of your account. You can re-enable it anytime.</p>
            </div>

            <div className="form-group">
              <label className="form-label">Verification Code</label>
              <input
                type="text"
                className="form-input code-input"
                maxLength="6"
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={loading}
              />
            </div>

            <div className="button-group">
              <button
                className="btn-danger"
                onClick={handleDisableTOTP}
                disabled={loading || disableCode.length !== 6}
              >
                <X size={18} /> Disable 2FA
              </button>
              <button
                className="btn-ghost"
                onClick={() => {
                  setSetupStep('status');
                  setDisableCode('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </section>
      )}

      <style jsx>{`
        .totp-section {
          max-width: 600px;
          margin: 0 auto;
        }

        .status-card,
        .setup-card {
          background: #F8F9FA;
          border: none;
          box-shadow: 5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85);
          border-radius: 8px;
          padding: 32px;
        }

        .status-header {
          display: flex;
          align-items: flex-start;
          gap: 20px;
          margin-bottom: 24px;
        }

        .status-header > div {
          flex: 1;
        }

        .status-header h2 {
          margin: 0 0 8px 0;
          font-size: 24px;
          font-weight: 600;
          color: #1f2937;
        }

        .status-header p {
          margin: 0;
          font-size: 16px;
        }

        .status-enabled {
          color: #16a34a;
          font-weight: 600;
        }

        .status-disabled {
          color: #DC3545;
          font-weight: 600;
        }

        .step-header {
          margin-bottom: 24px;
        }

        .step-header h2 {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
        }

        .step-header p {
          margin: 0;
          color: #6C757D;
          font-size: 14px;
        }

        .step-header.danger h2 {
          color: #DC3545;
        }

        .info-box {
          background: #eff6ff;
          border-left: none;
          box-shadow: inset 2px 0 4px rgba(46, 139, 152, 0.3);
          padding: 16px;
          border-radius: 4px;
          margin-bottom: 24px;
        }

        .info-box p {
          margin: 0;
          color: #1e40af;
          font-size: 14px;
        }

        .success-box {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #f0fdf4;
          border-left: none;
          box-shadow: inset 2px 0 4px rgba(22, 163, 74, 0.3);
          padding: 16px;
          border-radius: 4px;
          margin-bottom: 24px;
        }

        .success-box p {
          margin: 0;
          color: #15803d;
          font-size: 14px;
        }

        .warning-box {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          background: #fef3c7;
          border-left: none;
          box-shadow: inset 2px 0 4px rgba(242, 101, 34, 0.3);
          padding: 16px;
          border-radius: 4px;
          margin-bottom: 24px;
        }

        .warning-box.danger {
          background: #fee2e2;
          box-shadow: inset 2px 0 4px rgba(220, 53, 69, 0.3);
        }

        .warning-box p {
          margin: 0;
          color: #92400e;
          font-size: 14px;
        }

        .warning-box.danger p {
          color: #991b1b;
        }

        .secret-section {
          background: #f9fafb;
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 24px;
          border: none;
          box-shadow: 3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85);
        }

        .secret-item {
          margin-bottom: 16px;
        }

        .secret-item:last-child {
          margin-bottom: 0;
        }

        .secret-item label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #6C757D;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .secret-display {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #F8F9FA;
          padding: 12px;
          border-radius: 4px;
          border: none;
          box-shadow: inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85);
        }

        .secret-display code {
          flex: 1;
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: 1px;
        }

        .code-input {
          font-size: 24px;
          letter-spacing: 8px;
          text-align: center;
          font-weight: 600;
          font-family: 'Monaco', 'Courier New', monospace;
        }

        .action-buttons,
        .button-group {
          display: flex;
          gap: 12px;
        }

        .action-buttons {
          margin-top: 24px;
        }

        .button-group {
          margin-top: 24px;
        }

        .backup-codes-container {
          background: #f9fafb;
          border-radius: 6px;
          padding: 16px;
          margin-bottom: 24px;
          border: none;
          box-shadow: 3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85);
        }

        .backup-codes-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          margin-bottom: 16px;
        }

        @media (max-width: 500px) {
          .backup-codes-list {
            grid-template-columns: 1fr;
          }
        }

        .backup-code {
          background: #F8F9FA;
          border: none;
          box-shadow: inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85);
          border-radius: 4px;
          padding: 12px;
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          text-align: center;
          letter-spacing: 1px;
        }

        .loading-text {
          text-align: center;
          padding: 48px;
          color: #6C757D;
          font-size: 16px;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
        }
      `}</style>
    </div>
  );
}
